/**
 * 내 프로필 — 비밀번호 변경 모달.
 * Figma「11 비밀번호 변경 (모달)」(12679:5009).
 */
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Field } from '@/shared/components/ui/form-field';
import { PasswordInput } from '@/shared/components/ui/password-input';
import {
  areChangePasswordRulesMet,
  evaluateChangePasswordRules,
} from '@/shared/lib/forceChangePasswordRules';
import { cn } from '@/shared/lib/utils';
import { Check, Circle, Info } from 'lucide-react';
import React, { useEffect, useState } from 'react';

export type ChangePasswordPayload = {
  currentPassword: string;
  newPassword: string;
};

export type ChangePasswordModalProps = {
  open: boolean;
  onClose: () => void;
  /** API 연동 전 stub — 성공 처리만 호출 */
  onSubmit: (payload: ChangePasswordPayload) => void | Promise<void>;
};

type RuleRowProps = {
  met: boolean;
  label: string;
};

const RuleRow: React.FC<RuleRowProps> = ({ met, label }) => (
  <div
    className={cn(
      'flex items-center gap-2 text-xs',
      met ? 'text-foreground' : 'text-muted-foreground',
    )}
  >
    {met ? (
      <Check
        className="h-3.5 w-3.5 shrink-0 text-ruda-success"
        strokeWidth={3}
        aria-hidden
      />
    ) : (
      <Circle className="h-3.5 w-3.5 shrink-0" aria-hidden />
    )}
    <span>{label}</span>
  </div>
);

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSubmitting(false);
    }
  }, [open]);

  const rules = evaluateChangePasswordRules(newPassword, {
    currentPassword,
  });
  const rulesMet = areChangePasswordRulesMet(rules);
  const confirmMatches =
    confirmPassword.length > 0 && newPassword === confirmPassword;
  const newPasswordValid = rulesMet;
  const confirmValid = rulesMet && confirmMatches;

  const canSubmit =
    currentPassword.length > 0 &&
    rulesMet &&
    confirmMatches &&
    !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit({ currentPassword, newPassword });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-[480px] gap-4">
        <DialogHeader>
          <DialogTitle>비밀번호 변경</DialogTitle>
        </DialogHeader>

        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          <Field label="현재 비밀번호" htmlFor="change-pw-current">
            <PasswordInput
              id="change-pw-current"
              size="sm"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="현재 비밀번호"
            />
          </Field>

          <Field label="새 비밀번호" htmlFor="change-pw-new">
            <PasswordInput
              id="change-pw-new"
              size="sm"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              tone={newPasswordValid ? 'success' : 'default'}
              placeholder="새 비밀번호"
              trailing={
                newPasswordValid ? (
                  <Check
                    className="h-4 w-4 text-ruda-success"
                    strokeWidth={3}
                    aria-hidden
                  />
                ) : null
              }
            />
          </Field>

          <Field label="새 비밀번호 확인" htmlFor="change-pw-confirm">
            <PasswordInput
              id="change-pw-confirm"
              size="sm"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              tone={confirmValid ? 'success' : 'default'}
              placeholder="새 비밀번호 재입력"
              trailing={
                confirmValid ? (
                  <Check
                    className="h-4 w-4 text-ruda-success"
                    strokeWidth={3}
                    aria-hidden
                  />
                ) : null
              }
            />
          </Field>

          <div className="flex flex-col gap-2 rounded-12 bg-muted/80 px-4 py-3.5">
            <RuleRow met={rules.minLength} label="10자 이상" />
            <RuleRow
              met={rules.complexity}
              label="영문 대/소문자·숫자·특수문자 중 3종 이상"
            />
            <RuleRow
              met={rules.differentFromRecent}
              label="직전 3개 비밀번호와 다름"
            />
          </div>

          <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>변경 시 다른 기기의 로그인 세션이 모두 만료됩니다.</span>
          </p>

          <DialogFooter className="sm:justify-end [&_button]:h-auto [&_button]:flex-none [&_button]:rounded-8 [&_button]:px-[18px] [&_button]:py-2.5 [&_button]:text-[13px]">
            <Button type="button" variant="outline" onClick={onClose}>
              취소
            </Button>
            <Button
              type="submit"
              className="ruda-button"
              disabled={!canSubmit}
              loading={submitting}
            >
              변경
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
